import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, ArrowLeft, Globe } from 'lucide-react';

// Politique de confidentialité — conforme Loi 25 (Québec) + RGPD (UE)
// Bilingue FR/EN. Page publique, accessible sans authentification.

const CONTENT = {
  fr: {
    title: 'Politique de confidentialité',
    subtitle: 'En vigueur depuis février 2026',
    lastUpdate: 'Dernière mise à jour : 9 février 2026',
    sections: [
      {
        h: '1. Qui sommes-nous ?',
        p: [
          "ProPilote (\"l'Application\", \"nous\", \"notre\") est une plateforme SaaS de gestion de flotte de transport éditée par Express Pat Morin Inc., société immatriculée au Québec, Canada (NEQ disponible sur demande), ayant son siège à Drummondville (Québec).",
          "Pour toute question concernant cette politique ou l'exercice de vos droits, contactez notre responsable de la protection des renseignements personnels :",
          "Express Pat Morin Inc. — apps@expresspatmorin.ca — Tél. : +1 (514) 444-2354.",
        ],
      },
      {
        h: '2. Renseignements personnels que nous recueillons',
        p: [
          "Nous recueillons uniquement les renseignements nécessaires à la prestation de nos services :",
        ],
        list: [
          "Identification : nom, prénom, courriel professionnel, numéro de téléphone, photo de profil (facultative).",
          "Identifiants techniques : identifiant utilisateur, organisation/transporteur, rôle (chauffeur, dispatcher, mécanicien, administrateur).",
          "Données d'usage : rapports quotidiens, inspections SAAQ, heures de service, kilométrage, notes au dispatch.",
          "Documents : numérisations de bordereaux de transport (BOL), photos de défauts mécaniques, signatures électroniques.",
          "Géolocalisation : coordonnées GPS au moment des signatures de livraison, à des fins de preuve uniquement.",
          "Données techniques : adresse IP, type d'appareil, système d'exploitation, version de l'application, journaux d'erreurs.",
        ],
      },
      {
        h: '3. Pourquoi traitons-nous ces renseignements ?',
        p: [
          "Vos renseignements sont traités pour les finalités suivantes :",
        ],
        list: [
          "Exécution du contrat : authentification, fonctionnement de l'application, communication avec le dispatch.",
          "Conformité réglementaire : tenue des registres exigés par la SAAQ et les règlements fédéraux sur le transport routier.",
          "Sécurité : prévention de la fraude, surveillance des accès, intégrité des données.",
          "Amélioration du service : analyses agrégées et anonymisées d'utilisation.",
          "Support client : réponse à vos demandes et résolution d'incidents.",
        ],
      },
      {
        h: '4. Base légale du traitement',
        p: [
          "Nous traitons vos renseignements sur les bases légales suivantes : exécution d'un contrat (vous ou votre employeur), respect d'obligations légales (conformité SAAQ, comptabilité), intérêts légitimes (sécurité, prévention de la fraude) et, lorsque requis, votre consentement explicite (notifications push, géolocalisation).",
        ],
      },
      {
        h: '5. Durée de conservation',
        p: [
          "Nous conservons vos renseignements aussi longtemps que nécessaire aux finalités décrites :",
        ],
        list: [
          "Données opérationnelles (rapports, inspections, BOL) : 7 ans (obligation comptable et fiscale canadienne).",
          "Compte utilisateur : durée de la relation contractuelle + 12 mois après désactivation.",
          "Journaux techniques : 90 jours maximum.",
          "Données anonymisées (statistiques) : sans limite de durée.",
        ],
      },
      {
        h: '6. Partage avec des tiers',
        p: [
          "Nous ne vendons jamais vos renseignements personnels. Nous ne les partageons qu'avec des sous-traitants strictement nécessaires :",
        ],
        list: [
          "Hébergement infonuagique : fournisseur certifié (serveurs au Canada / États-Unis).",
          "Génération de PDF et reconnaissance IA des documents : services de traitement liés par contrat de confidentialité.",
          "Notifications push : Apple (APNs) et Google (FCM).",
          "Paiement (administrateurs uniquement) : Stripe Inc., conforme PCI-DSS.",
          "Autorités publiques : uniquement sur demande légale valide (subpoena, mandat).",
        ],
        p2: [
          "Tous nos sous-traitants sont liés par contrat à des engagements de confidentialité au moins équivalents à la présente politique.",
        ],
      },
      {
        h: '7. Transferts hors du Québec et hors du Canada',
        p: [
          "Certaines données peuvent être traitées hors du Québec ou hors du Canada (notamment aux États-Unis pour l'hébergement et l'IA). Conformément à la Loi 25, nous avons procédé à une évaluation des facteurs relatifs à la vie privée (ÉFVP) et mis en place les protections contractuelles requises. Vous pouvez en demander une copie à apps@expresspatmorin.ca.",
        ],
      },
      {
        h: '8. Vos droits',
        p: [
          "Vous bénéficiez des droits suivants en vertu de la Loi 25 et du RGPD :",
        ],
        list: [
          "Droit d'accès : obtenir une copie de vos renseignements.",
          "Droit de rectification : corriger des informations inexactes.",
          "Droit à l'effacement : demander la suppression de vos données (sous réserve des obligations légales de conservation).",
          "Droit à la portabilité : recevoir vos données dans un format structuré.",
          "Droit d'opposition : refuser certains traitements (marketing, analyses).",
          "Droit à la désindexation et à la cessation de la diffusion (Loi 25).",
          "Droit de retirer votre consentement à tout moment.",
          "Droit de déposer une plainte auprès de la Commission d'accès à l'information du Québec (cai.gouv.qc.ca).",
        ],
        p2: [
          "Pour exercer ces droits, écrivez à apps@expresspatmorin.ca. Nous répondons dans un délai maximal de 30 jours.",
        ],
      },
      {
        h: '9. Sécurité',
        p: [
          "Nous mettons en œuvre des mesures techniques et organisationnelles appropriées : chiffrement TLS 1.3 en transit, chiffrement AES-256 au repos, hachage bcrypt des mots de passe, authentification à deux facteurs, journalisation des accès, sauvegardes quotidiennes, audits de sécurité réguliers.",
          "En cas d'incident de confidentialité présentant un risque sérieux, nous vous en informerons et notifierons la CAI dans les délais prévus par la loi.",
        ],
      },
      {
        h: '10. Cookies et technologies similaires',
        p: [
          "L'application mobile n'utilise pas de cookies. Le portail web utilise uniquement des cookies strictement nécessaires (session, sécurité). Aucun cookie publicitaire ou de suivi tiers.",
        ],
      },
      {
        h: '11. Confidentialité des mineurs',
        p: [
          "Nos services sont destinés à un usage professionnel et ne s'adressent pas aux personnes de moins de 16 ans. Nous ne collectons pas sciemment de renseignements concernant des mineurs.",
        ],
      },
      {
        h: '12. Modifications',
        p: [
          "Nous pouvons modifier cette politique pour refléter des évolutions légales ou techniques. Toute modification importante vous sera notifiée par courriel ou notification dans l'application au moins 30 jours avant son entrée en vigueur.",
        ],
      },
      {
        h: '13. Contact',
        p: [
          "Express Pat Morin Inc.",
          "Drummondville, Québec, Canada",
          "Courriel : apps@expresspatmorin.ca",
          "Téléphone : +1 (514) 444-2354",
          "Site : https://propilote.ca",
        ],
      },
    ],
  },
  en: {
    title: 'Privacy Policy',
    subtitle: 'Effective February 2026',
    lastUpdate: 'Last updated: February 9, 2026',
    sections: [
      {
        h: '1. Who we are',
        p: [
          "ProPilote (\"the App\", \"we\", \"our\") is a fleet management SaaS platform operated by Express Pat Morin Inc., a corporation registered in Quebec, Canada, with its head office in Drummondville (Quebec).",
          "For any question regarding this policy or to exercise your rights, contact our Privacy Officer:",
          "Express Pat Morin Inc. — apps@expresspatmorin.ca — Phone: +1 (514) 444-2354.",
        ],
      },
      {
        h: '2. Personal information we collect',
        p: [
          "We collect only information necessary to deliver our services:",
        ],
        list: [
          "Identification: first/last name, business email, phone number, optional profile photo.",
          "Technical identifiers: user ID, organization/carrier, role (driver, dispatcher, mechanic, administrator).",
          "Usage data: daily reports, SAAQ inspections, hours of service, mileage, dispatch notes.",
          "Documents: bill-of-lading scans, mechanical defect photos, electronic signatures.",
          "Geolocation: GPS coordinates captured during delivery signatures, for proof purposes only.",
          "Technical data: IP address, device type, OS, app version, error logs.",
        ],
      },
      {
        h: '3. Why we process this information',
        p: [
          "Your information is processed for the following purposes:",
        ],
        list: [
          "Contract performance: authentication, app functionality, dispatch communication.",
          "Regulatory compliance: SAAQ records and federal road transport regulations.",
          "Security: fraud prevention, access monitoring, data integrity.",
          "Service improvement: aggregated, anonymized usage analytics.",
          "Customer support: responding to your requests and resolving incidents.",
        ],
      },
      {
        h: '4. Legal basis for processing',
        p: [
          "We process your information based on: contract performance (you or your employer), legal obligations (SAAQ compliance, accounting), legitimate interests (security, fraud prevention) and, where required, your explicit consent (push notifications, geolocation).",
        ],
      },
      {
        h: '5. Retention period',
        p: [
          "We retain your information only as long as necessary for the stated purposes:",
        ],
        list: [
          "Operational data (reports, inspections, BOLs): 7 years (Canadian accounting/tax obligation).",
          "User account: duration of the contractual relationship + 12 months after deactivation.",
          "Technical logs: 90 days maximum.",
          "Anonymized data (statistics): unlimited duration.",
        ],
      },
      {
        h: '6. Sharing with third parties',
        p: [
          "We never sell your personal information. We share it only with strictly necessary subprocessors:",
        ],
        list: [
          "Cloud hosting: certified provider (servers in Canada / United States).",
          "PDF generation and document AI recognition: contractually bound providers.",
          "Push notifications: Apple (APNs) and Google (FCM).",
          "Payment (administrators only): Stripe Inc., PCI-DSS compliant.",
          "Public authorities: only upon valid legal request (subpoena, warrant).",
        ],
        p2: [
          "All our subprocessors are bound by confidentiality commitments at least equivalent to this policy.",
        ],
      },
      {
        h: '7. Transfers outside Quebec and Canada',
        p: [
          "Some data may be processed outside Quebec or Canada (notably in the U.S. for hosting and AI). Pursuant to Quebec Law 25, we conducted a Privacy Impact Assessment (PIA) and have implemented required contractual safeguards. A copy is available on request at apps@expresspatmorin.ca.",
        ],
      },
      {
        h: '8. Your rights',
        p: [
          "You have the following rights under Quebec Law 25 and the GDPR:",
        ],
        list: [
          "Right of access: obtain a copy of your data.",
          "Right to rectification: correct inaccurate information.",
          "Right to erasure: request deletion (subject to legal retention obligations).",
          "Right to portability: receive your data in a structured format.",
          "Right to object: refuse certain processing (marketing, analytics).",
          "Right to deindexation and to cease distribution (Law 25).",
          "Right to withdraw consent at any time.",
          "Right to file a complaint with the Quebec Access to Information Commission (cai.gouv.qc.ca).",
        ],
        p2: [
          "To exercise these rights, email apps@expresspatmorin.ca. We respond within 30 days maximum.",
        ],
      },
      {
        h: '9. Security',
        p: [
          "We implement appropriate technical and organizational measures: TLS 1.3 in transit, AES-256 at rest, bcrypt password hashing, two-factor authentication, access logging, daily backups, regular security audits.",
          "In the event of a privacy incident posing serious risk, we will notify you and the CAI within legal deadlines.",
        ],
      },
      {
        h: '10. Cookies and similar technologies',
        p: [
          "The mobile app does not use cookies. The web portal uses only strictly necessary cookies (session, security). No advertising or third-party tracking cookies.",
        ],
      },
      {
        h: '11. Children\u2019s privacy',
        p: [
          "Our services are intended for professional use and not directed at people under 16. We do not knowingly collect information from minors.",
        ],
      },
      {
        h: '12. Changes',
        p: [
          "We may update this policy to reflect legal or technical changes. Any material change will be notified by email or in-app notification at least 30 days before taking effect.",
        ],
      },
      {
        h: '13. Contact',
        p: [
          "Express Pat Morin Inc.",
          "Drummondville, Quebec, Canada",
          "Email: apps@expresspatmorin.ca",
          "Phone: +1 (514) 444-2354",
          "Web: https://propilote.ca",
        ],
      },
    ],
  },
};

export const PrivacyPage = () => {
  const navigate = useNavigate();
  const [lang, setLang] = useState('fr');
  const c = CONTENT[lang];

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100" data-testid="privacy-page">
      <div className="sticky top-0 z-10 bg-slate-950/95 backdrop-blur border-b border-slate-800">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-slate-800 rounded-lg"
            data-testid="privacy-back-btn"
          >
            <ArrowLeft size={22} />
          </button>
          <Shield className="text-amber-500" size={24} />
          <h1 className="text-lg font-semibold flex-1 truncate">{c.title}</h1>
          <button
            onClick={() => setLang(lang === 'fr' ? 'en' : 'fr')}
            className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-sm"
            data-testid="privacy-lang-toggle"
          >
            <Globe size={16} />
            {lang === 'fr' ? 'EN' : 'FR'}
          </button>
        </div>
      </div>

      <article className="max-w-3xl mx-auto px-4 py-8 space-y-6">
        <header className="space-y-2">
          <p className="text-amber-500 text-sm font-medium uppercase tracking-wider">
            {c.subtitle}
          </p>
          <h2 className="text-3xl font-bold">{c.title}</h2>
          <p className="text-slate-400 text-sm">{c.lastUpdate}</p>
        </header>

        {c.sections.map((s, i) => (
          <section key={i} className="space-y-3">
            <h3 className="text-xl font-semibold text-white">{s.h}</h3>
            {s.p?.map((para, j) => (
              <p key={j} className="text-slate-300 leading-relaxed">{para}</p>
            ))}
            {s.list && (
              <ul className="space-y-2 ml-4">
                {s.list.map((item, j) => (
                  <li key={j} className="text-slate-300 leading-relaxed list-disc list-outside ml-2">
                    {item}
                  </li>
                ))}
              </ul>
            )}
            {s.p2?.map((para, j) => (
              <p key={`p2-${j}`} className="text-slate-300 leading-relaxed">{para}</p>
            ))}
          </section>
        ))}

        <footer className="pt-8 border-t border-slate-800 text-slate-500 text-sm text-center">
          © 2026 Express Pat Morin Inc. — ProPilote
        </footer>
      </article>
    </div>
  );
};

export default PrivacyPage;
